import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faFilePdf } from '@fortawesome/free-solid-svg-icons';
import DocumentBrowser from '../Repository/DocumentBrowser';
import './ConsultationForm.css';

const ConsultationForm = ({ consultation, onSubmit, onClose, repositoryItems, documentTypes, workingGroups, onUploadDocument }) => {
    const [formData, setFormData] = useState({
        name: '',
        topic: '',
        category: 'Consultation',
        status: 'Completed',
        completed: '',
        wg: '',
        link: '',
        longFileName: '',
        ib: '',
        year: new Date().getFullYear(),
        shortDescription: ''
    });
    const [showDocumentBrowser, setShowDocumentBrowser] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState(null);

    useEffect(() => {
        if (consultation) {
            setFormData(consultation);
            if (consultation.link && consultation.longFileName) {
                const doc = repositoryItems.find(item => item.link === consultation.link);
                if (doc) {
                    setSelectedDocument(doc);
                }
            }
        }
    }, [consultation, repositoryItems]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit(formData);
    };

    const handleDocumentSelect = (document) => {
        setSelectedDocument(document);
        setFormData(prev => ({
            ...prev,
            link: document.link,
            longFileName: document.fileName
        }));
        setShowDocumentBrowser(false);
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="consultation-form" onClick={e => e.stopPropagation()}>
                <div className="form-header">
                    <h2>{consultation ? 'Edit Consultation' : 'Add Consultation'}</h2>
                    <button className="close-button" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="form-body">
                        <div className="form-group">
                            <label htmlFor="name">Name</label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="topic">Topic</label>
                            <textarea
                                id="topic"
                                name="topic"
                                value={formData.topic}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="category">Category</label>
                                <select
                                    id="category"
                                    name="category"
                                    value={formData.category}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="Consultation">Consultation</option>
                                    <option value="Submission">Submission</option>
                                    <option value="Paper">Paper</option>
                                </select>
                            </div>

                            <div className="form-group">
                                <label htmlFor="status">Status</label>
                                <select
                                    id="status"
                                    name="status"
                                    value={formData.status}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="Completed">Completed</option>
                                    <option value="In Progress">In Progress</option>
                                    <option value="Draft">Draft</option>
                                </select>
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="completed">Completed Date</label>
                                <input
                                    type="date"
                                    id="completed"
                                    name="completed"
                                    value={formData.completed}
                                    onChange={handleChange}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="year">Year</label>
                                <input
                                    type="number"
                                    id="year"
                                    name="year"
                                    value={formData.year}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="wg">Working Group</label>
                                <input
                                    type="text"
                                    id="wg"
                                    name="wg"
                                    value={formData.wg}
                                    onChange={handleChange}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="ib">Issuing Body</label>
                                <input
                                    type="text"
                                    id="ib"
                                    name="ib"
                                    value={formData.ib}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Document</label>
                            {selectedDocument ? (
                                <div className="selected-document">
                                    <FontAwesomeIcon icon={faFilePdf} className="file-icon" />
                                    <div className="document-info">
                                        <div className="document-title">{selectedDocument.title}</div>
                                        <div className="document-filename">{selectedDocument.fileName}</div>
                                    </div>
                                    <button 
                                        type="button" 
                                        className="btn-change" 
                                        onClick={() => setShowDocumentBrowser(true)}
                                    >
                                        Change
                                    </button>
                                </div>
                            ) : (
                                <button 
                                    type="button" 
                                    className="btn-select-document" 
                                    onClick={() => setShowDocumentBrowser(true)}
                                >
                                    <FontAwesomeIcon icon={faFilePdf} /> Select Document
                                </button>
                            )}
                        </div>

                        <div className="form-group">
                            <label htmlFor="shortDescription">Short Description</label>
                            <textarea
                                id="shortDescription"
                                name="shortDescription"
                                value={formData.shortDescription}
                                onChange={handleChange}
                                required
                            />
                        </div>
                    </div>

                    <div className="form-actions">
                        <button type="button" className="btn-secondary" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className="btn-primary" disabled={!selectedDocument}>
                            {consultation ? 'Update' : 'Create'} Consultation
                        </button>
                    </div>
                </form>
            </div>

            {showDocumentBrowser && (
                <DocumentBrowser
                    documents={repositoryItems}
                    documentTypes={documentTypes}
                    workingGroups={workingGroups}
                    onSelect={handleDocumentSelect}
                    onClose={() => setShowDocumentBrowser(false)}
                    onUpload={onUploadDocument}
                />
            )}
        </div>
    );
};

export default ConsultationForm; 